/**
 * Lista de tenants com CRUD inline para superadmin.
 *
 * - Busca textual (nome do app, candidato, slug)
 * - Filtro por status (todos / ativos / inativos)
 * - Card por tenant com: avatar, nome, candidato, slug, plano, contagem de membros, status
 * - Ações por linha: Visualizar, Editar, Ativar/Inativar, Excluir
 * - Botão "Novo tenant" no topo
 */

import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Building2, Plus, Search, Pencil, Trash2, Eye, ChevronRight, Clock,
  CheckCircle2, XCircle, Power, AlertTriangle, Loader2, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useAllTenants, useDeleteTenant, useToggleTenantActive,
  type TenantRow, type PlanName,
} from '../hooks/useTenantsAdmin';
import { TenantFormModal } from './TenantFormModal';

const PLAN_COLORS: Record<PlanName, string> = {
  essencial:  'bg-slate-500/15 text-slate-300 border-slate-500/30',
  pro:        'bg-blue-500/15  text-blue-300  border-blue-500/30',
  enterprise: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
};

interface TenantsListProps {
  onView?: (tenantId: string) => void;
}

export function TenantsList({ onView }: TenantsListProps) {
  const { data: tenants = [], isLoading } = useAllTenants();
  const toggleActive = useToggleTenantActive();
  const deleteTenant = useDeleteTenant();

  const [query,    setQuery]    = useState('');
  const [filter,   setFilter]   = useState<'all' | 'active' | 'inactive'>('all');
  const [editing,  setEditing]  = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TenantRow | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tenants.filter((t) => {
      if (filter === 'active'   && !t.active) return false;
      if (filter === 'inactive' &&  t.active) return false;
      if (!q) return true;
      const blob = [
        t.slug,
        t.tenant_branding?.app_name,
        t.tenant_branding?.candidate_name,
        t.tenant_branding?.party,
      ].filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [tenants, query, filter]);

  const handleToggle = (t: TenantRow) => {
    toggleActive.mutate(
      { id: t.id, active: !t.active },
      {
        onSuccess: () => toast.success(t.active ? 'Tenant desativado' : 'Tenant ativado'),
        onError:   (e) => toast.error('Falha ao alterar status', { description: String(e) }),
      },
    );
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteTenant.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success(`Tenant "${deleteTarget.tenant_branding?.app_name ?? deleteTarget.slug}" removido`);
        setDeleteTarget(null);
      },
      onError: (e) => toast.error('Falha ao excluir', { description: String(e) }),
    });
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        {/* Busca */}
        <div className="relative flex-grow">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-subtle pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nome, candidato ou slug…"
            className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-midnight-2/60 border border-hairline text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 transition-all"
          />
        </div>

        {/* Filtro de status */}
        <div className="flex gap-1 p-1 rounded-xl bg-midnight-3 border border-hairline">
          {([['all', 'Todos'], ['active', 'Ativos'], ['inactive', 'Inativos']] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                filter === key
                  ? 'bg-accent text-midnight'
                  : 'text-ink-muted hover:text-ink'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Novo */}
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="flex items-center gap-2 rounded-xl btn-accent px-4 py-2.5 text-sm font-semibold cursor-pointer shrink-0"
        >
          <Plus className="h-4 w-4" />
          Novo tenant
        </button>
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl card-premium h-32 animate-pulse bg-midnight-4/30" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-hairline p-12 text-center">
          <Building2 className="h-8 w-8 text-ink-subtle mx-auto mb-3" />
          <p className="text-sm text-ink-muted">
            {query || filter !== 'all'
              ? 'Nenhum tenant corresponde aos filtros.'
              : 'Nenhum tenant cadastrado ainda.'}
          </p>
          {!query && filter === 'all' && (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="mt-4 text-xs font-semibold text-accent hover:underline cursor-pointer"
            >
              Criar o primeiro tenant →
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((t) => (
            <TenantCard
              key={t.id}
              tenant={t}
              onView={onView ? () => onView(t.id) : undefined}
              onEdit={() => setEditing(t.id)}
              onToggle={() => handleToggle(t)}
              onDelete={() => setDeleteTarget(t)}
              isToggling={toggleActive.isPending && toggleActive.variables?.id === t.id}
            />
          ))}
        </div>
      )}

      {/* Modais */}
      <TenantFormModal
        open={creating}
        onClose={() => setCreating(false)}
      />
      <TenantFormModal
        open={!!editing}
        tenantId={editing}
        onClose={() => setEditing(null)}
      />

      {/* Confirm delete */}
      {deleteTarget && (
        <DeleteConfirmDialog
          tenant={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          isDeleting={deleteTenant.isPending}
        />
      )}
    </div>
  );
}

// ── Card de tenant ───────────────────────────────────────────────────────────

function TenantCard({
  tenant, onView, onEdit, onToggle, onDelete, isToggling,
}: {
  tenant:     TenantRow;
  onView?:    () => void;
  onEdit:     () => void;
  onToggle:   () => void;
  onDelete:   () => void;
  isToggling: boolean;
}) {
  const branding = tenant.tenant_branding;
  const label    = branding?.app_name || tenant.slug;
  const sub      = branding?.candidate_name;
  const party    = branding?.party;
  const initial  = (label[0] ?? 'L').toUpperCase();

  const avatarStyle = branding
    ? { background: `${branding.primary_color}22`, color: branding.accent_color, border: `1px solid ${branding.accent_color}33` }
    : { background: 'rgba(0,194,255,0.12)', color: '#00C2FF', border: '1px solid rgba(0,194,255,0.25)' };

  return (
    <div
      className={`group relative rounded-2xl card-premium p-5 transition-all ${
        !tenant.active ? 'opacity-60' : 'hover:border-accent/30'
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="h-11 w-11 rounded-xl flex items-center justify-center text-base font-black shrink-0"
            style={avatarStyle}
          >
            {initial}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-bold text-ink truncate">{label}</h3>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border capitalize ${PLAN_COLORS[tenant.plan]}`}>
                {tenant.plan}
              </span>
            </div>
            {sub && (
              <p className="text-xs text-ink-muted mt-0.5 truncate">
                {sub}{party ? ` · ${party}` : ''}
              </p>
            )}
            <code className="text-[10px] text-ink-subtle font-mono">{tenant.slug}</code>
          </div>
        </div>

        {/* Status indicator */}
        <div className="shrink-0">
          {tenant.active
            ? <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            : <XCircle className="h-4 w-4 text-danger" />
          }
        </div>
      </div>

      {/* Meta row */}
      <div className="flex items-center gap-3 text-[10px] text-ink-subtle">
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {new Date(tenant.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
        <span className="flex items-center gap-1">
          <Users className="h-3 w-3" />
          {tenant.member_count ?? 0} {tenant.member_count === 1 ? 'membro' : 'membros'}
        </span>
      </div>

      {/* Ações */}
      <div className="mt-4 hairline-t pt-3 flex items-center justify-between">
        <div className="flex items-center gap-1">
          {onView && (
            <ActionBtn icon={Eye} label="Visualizar" onClick={onView} />
          )}
          <ActionBtn icon={Pencil} label="Editar" onClick={onEdit} />
          <ActionBtn
            icon={Power}
            label={tenant.active ? 'Desativar' : 'Ativar'}
            onClick={onToggle}
            loading={isToggling}
            tone={tenant.active ? 'amber' : 'emerald'}
          />
          <ActionBtn icon={Trash2} label="Excluir" onClick={onDelete} tone="danger" />
        </div>

        {onView && (
          <button
            onClick={onView}
            className="flex items-center gap-1.5 text-xs font-semibold text-accent hover:underline cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
          >
            Abrir
            <ChevronRight className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function ActionBtn({
  icon: Icon, label, onClick, loading, tone = 'default',
}: {
  icon:    typeof Eye;
  label:   string;
  onClick: () => void;
  loading?: boolean;
  tone?:   'default' | 'danger' | 'amber' | 'emerald';
}) {
  const toneClass = {
    default:  'text-ink-subtle hover:text-ink hover:bg-white/5',
    danger:   'text-ink-subtle hover:text-danger hover:bg-danger/10',
    amber:    'text-ink-subtle hover:text-amber-300 hover:bg-amber-500/10',
    emerald:  'text-ink-subtle hover:text-emerald-300 hover:bg-emerald-500/10',
  }[tone];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      title={label}
      className={`rounded-lg p-1.5 transition-colors cursor-pointer disabled:opacity-50 ${toneClass}`}
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
    </button>
  );
}

// ── Dialog de confirmação de exclusão ────────────────────────────────────────

function DeleteConfirmDialog({
  tenant, onCancel, onConfirm, isDeleting,
}: {
  tenant:     TenantRow;
  onCancel:   () => void;
  onConfirm:  () => void;
  isDeleting: boolean;
}) {
  const [typed, setTyped] = useState('');
  const expected = tenant.slug;
  const matches  = typed.trim() === expected;

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative w-full max-w-md rounded-2xl glass-strong border border-danger/30 shadow-deep p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-10 w-10 rounded-xl bg-danger/15 border border-danger/30 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-5 w-5 text-danger" />
          </div>
          <div className="flex-grow">
            <h3 className="font-display text-base font-bold text-ink">Excluir tenant?</h3>
            <p className="text-xs text-ink-muted mt-1">
              Esta ação <strong className="text-danger">remove permanentemente</strong> o tenant{' '}
              <code className="bg-midnight-3 px-1.5 py-0.5 rounded font-mono text-[11px]">{tenant.slug}</code>{' '}
              e todos os dados associados (contatos, campanhas, eventos, membros, configurações).
            </p>
          </div>
        </div>

        <div className="rounded-xl bg-danger/8 border border-danger/20 p-3 mb-4 text-xs text-ink-muted space-y-1">
          <p>• {tenant.member_count ?? 0} membros vinculados</p>
          <p>• Branding, integrações WhatsApp, planos e quotas</p>
          <p>• Cascata em contatos, campanhas, eventos, notas de voz e logs</p>
        </div>

        <label className="block mb-4">
          <span className="text-xs font-semibold text-ink-muted">
            Digite <code className="text-danger font-mono">{expected}</code> para confirmar:
          </span>
          <input
            autoFocus
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            className="mt-1.5 w-full rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2 text-sm font-mono text-ink outline-none focus:border-danger/40"
          />
        </label>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl btn-ghost px-4 py-2 text-sm font-medium cursor-pointer"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!matches || isDeleting}
            className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-all cursor-pointer disabled:opacity-40 disabled:cursor-default bg-danger/20 border border-danger/40 text-danger hover:bg-danger/30"
          >
            {isDeleting
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Excluindo…</>
              : <><Trash2 className="h-4 w-4" /> Excluir permanentemente</>
            }
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
